import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Shield, Key, Clock, Smartphone, Eye, EyeOff, Lock, Unlock, Settings, Plus, Edit, Trash2, Save, RefreshCw, TestTube, AlertCircle, CheckCircle2, XCircle, Users, Activity, BarChart3, PieChart, LineChart, TrendingUp, TrendingDown, Calendar, Filter, Search, Download, Upload, Copy, ExternalLink, Globe, Database, Server, HardDrive, Cpu, Network, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Lightning, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Bell, History, Star, Crown, Trophy, Medal, Gift, Coffee, Pizza, Cake, Music, Camera, Video, Share, Bookmark, Flag
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const LoginPolicies = () => {
  const [loginMetrics, setLoginMetrics] = useState(null);
  const [authenticationPolicies, setAuthenticationPolicies] = useState([]);
  const [passwordPolicies, setPasswordPolicies] = useState([]);
  const [sessionPolicies, setSessionPolicies] = useState([]);
  const [mfaSettings, setMfaSettings] = useState([]);
  const [policyTests, setPolicyTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h'); // Default to 24 hours
  const [policyFilter, setPolicyFilter] = useState('all'); // Default to all policies

  const loadLoginPolicyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricsResponse = await new AdminService().getLoginMetrics(timeRange, policyFilter);
      const authResponse = await new AdminService().getAuthenticationPolicies();
      const passwordResponse = await new AdminService().getPasswordPolicies();
      const sessionResponse = await new AdminService().getSessionPolicies();
      const mfaResponse = await new AdminService().getMfaSettings();
      const testsResponse = await new AdminService().getPolicyTests();

      if (metricsResponse.success && authResponse.success && passwordResponse.success && sessionResponse.success && mfaResponse.success && testsResponse.success) {
        setLoginMetrics(metricsResponse.data);
        setAuthenticationPolicies(authResponse.data);
        setPasswordPolicies(passwordResponse.data);
        setSessionPolicies(sessionResponse.data);
        setMfaSettings(mfaResponse.data);
        setPolicyTests(testsResponse.data);
      } else {
        setError(metricsResponse.message || authResponse.message || passwordResponse.message || sessionResponse.message || mfaResponse.message || testsResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch login policy data.');
      console.error('Error fetching login policy data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange, policyFilter]);

  useEffect(() => {
    loadLoginPolicyData();
    const interval = setInterval(loadLoginPolicyData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [loadLoginPolicyData]);

  const handleCreatePolicy = async (policyType, policyData) => {
    if (window.confirm(`Are you sure you want to create a new ${policyType} policy?`)) {
      try {
        const response = await new AdminService().createLoginPolicy(policyType, policyData);
        if (response.success) {
          alert(`${policyType} policy created successfully!`);
          loadLoginPolicyData();
        } else {
          alert(`Failed to create policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error creating policy.');
        console.error('Error creating policy:', err);
      }
    }
  };

  const handleUpdatePolicy = async (policyId, policyType, policyData) => {
    if (window.confirm(`Are you sure you want to update this ${policyType} policy?`)) {
      try {
        const response = await new AdminService().updateLoginPolicy(policyId, policyType, policyData);
        if (response.success) {
          alert(`${policyType} policy updated successfully!`);
          loadLoginPolicyData();
        } else {
          alert(`Failed to update policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating policy.');
        console.error('Error updating policy:', err);
      }
    }
  };

  const handleDeletePolicy = async (policyId, policyType) => {
    if (window.confirm(`Are you sure you want to delete this ${policyType} policy?`)) {
      try {
        const response = await new AdminService().deleteLoginPolicy(policyId, policyType);
        if (response.success) {
          alert(`${policyType} policy deleted successfully!`);
          loadLoginPolicyData();
        } else {
          alert(`Failed to delete policy: ${response.message}`);
        }
      } catch (err) {
        alert('Error deleting policy.');
        console.error('Error deleting policy:', err);
      }
    }
  };

  const handleTestPolicy = async (policyId, policyType) => {
    if (window.confirm(`Are you sure you want to test this ${policyType} policy?`)) {
      try {
        const response = await new AdminService().testLoginPolicy(policyId, policyType);
        if (response.success) {
          alert(`Policy test completed: ${response.data.result}`);
          loadLoginPolicyData();
        } else {
          alert(`Policy test failed: ${response.message}`);
        }
      } catch (err) {
        alert('Error testing policy.');
        console.error('Error testing policy:', err);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'testing': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading login policy data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!loginMetrics) {
    return <div className="p-6 text-center text-gray-500">No login policy data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Shield className="mr-3 text-blue-600" size={30} />
          Login Policies & Authentication Management
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <select
            value={policyFilter}
            onChange={(e) => setPolicyFilter(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Policies</option>
            <option value="authentication">Authentication</option>
            <option value="password">Password</option>
            <option value="session">Session</option>
            <option value="mfa">Multi-Factor Auth</option>
          </select>
          <button
            onClick={loadLoginPolicyData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Configure and manage login policies, authentication settings, password requirements, session management, and multi-factor authentication.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(loginMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Login Policy Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> Login Policy Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loginMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'active_policies' && <Shield size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'failed_attempts' && <AlertCircle size={40} className="text-red-500 opacity-70" />}
              {metric.type === 'success_rate' && <CheckCircle2 size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'mfa_enabled' && <Smartphone size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Authentication Policies */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Key className="mr-2 text-green-600" size={24} /> Authentication Policies
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Authentication Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('authentication', {})}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="mr-2" size={16} /> Add Policy
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Modified
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {authenticationPolicies.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No authentication policies configured.</td>
                </tr>
              ) : (
                authenticationPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {policy.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(policy.lastModified).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleTestPolicy(policy.id, 'authentication')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Test Policy"
                      >
                        <TestTube size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, 'authentication', policy)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id, 'authentication')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Password Policies */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Lock className="mr-2 text-orange-600" size={24} /> Password Policies
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Password Requirements</h3>
            <button
              onClick={() => handleCreatePolicy('password', {})}
              className="flex items-center px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              <Plus className="mr-2" size={16} /> Add Policy
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requirements
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Violations
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {passwordPolicies.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No password policies configured.</td>
                </tr>
              ) : (
                passwordPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div>Min Length: {policy.minLength} chars</div>
                        <div>Complexity: {policy.complexity}</div>
                        <div>Expiry: {policy.expiryDays} days</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.violations}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleTestPolicy(policy.id, 'password')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Test Policy"
                      >
                        <TestTube size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, 'password', policy)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id, 'password')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Session Management */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Clock className="mr-2 text-blue-600" size={24} /> Session Management
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Session Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('session', {})}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="mr-2" size={16} /> Add Policy
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timeout
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Concurrent Sessions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessionPolicies.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No session policies configured.</td>
                </tr>
              ) : (
                sessionPolicies.map((policy) => (
                  <tr key={policy.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{policy.name}</div>
                      <div className="text-sm text-gray-500">{policy.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.timeout} minutes</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{policy.concurrentSessions}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleTestPolicy(policy.id, 'session')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Test Policy"
                      >
                        <TestTube size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(policy.id, 'session', policy)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit Policy"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(policy.id, 'session')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Policy"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Multi-Factor Authentication */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Smartphone className="mr-2 text-purple-600" size={24} /> Multi-Factor Authentication
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">MFA Configuration</h3>
            <button
              onClick={() => handleCreatePolicy('mfa', {})}
              className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              <Plus className="mr-2" size={16} /> Add MFA Method
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users Enabled
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mfaSettings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No MFA methods configured.</td>
                </tr>
              ) : (
                mfaSettings.map((mfa) => (
                  <tr key={mfa.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{mfa.name}</div>
                      <div className="text-sm text-gray-500">{mfa.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {mfa.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mfa.usersEnabled}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(mfa.status)}`}>
                        {mfa.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleTestPolicy(mfa.id, 'mfa')}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Test MFA"
                      >
                        <TestTube size={18} />
                      </button>
                      <button
                        onClick={() => handleUpdatePolicy(mfa.id, 'mfa', mfa)}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Edit MFA"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(mfa.id, 'mfa')}
                        className="text-red-600 hover:text-red-900"
                        title="Delete MFA"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Policy Testing Results */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <TestTube className="mr-2 text-indigo-600" size={24} /> Policy Testing Results
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policy Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Result
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tested At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policyTests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No policy tests available.</td>
                </tr>
              ) : (
                policyTests.map((test) => (
                  <tr key={test.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{test.name}</div>
                      <div className="text-sm text-gray-500">{test.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {test.policyType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(test.result)}`}>
                        {test.result}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(test.testedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{test.details}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default LoginPolicies;